import '@geoapify/geocoder-autocomplete/styles/round-borders.css';
import { GeoapifyGeocoderAutocomplete } from '@geoapify/react-geocoder-autocomplete';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorType, Label, LabelType } from '../../_ui';
import { FinderOrigin } from './types';

interface GeoapifyPlace {
  properties?: { lat?: number; lon?: number; formatted?: string };
}

interface NearbyChargerAddressFieldProps {
  chargerOrigin: FinderOrigin | null;
  /** Shows the "use this charger's location" link */
  canResetToCharger: boolean;
  /** A suggestion was picked, or null when the input was cleared */
  onOriginSelect: (origin: FinderOrigin | null) => void;
  /** Text typed but not yet resolved to a suggestion */
  onUnresolvedInput: (text: string) => void;
  onResetToCharger: () => void;
}

const ADDRESS_DEBOUNCE_MS = 300;
const ADDRESS_SUGGESTION_LIMIT = 6;

const toFinderOrigin = (place: GeoapifyPlace | null): FinderOrigin | null => {
  const latitude = place?.properties?.lat;
  const longitude = place?.properties?.lon;
  if (latitude === undefined || longitude === undefined) return null;
  return {
    latitude,
    longitude,
    label: place?.properties?.formatted ?? `${latitude}, ${longitude}`,
  };
};

export const NearbyChargerAddressField = ({
  chargerOrigin,
  canResetToCharger,
  onOriginSelect,
  onUnresolvedInput,
  onResetToCharger,
}: NearbyChargerAddressFieldProps) => {
  const { t } = useTranslation();
  // The Geoapify input is uncontrolled, so remount it to restore the charger address
  const [inputKey, setInputKey] = useState(0);
  // Geoapify re-applies the bias whenever this object changes, so keep it stable
  const biasByProximity = useMemo(
    () =>
      chargerOrigin ? { lat: chargerOrigin.latitude, lon: chargerOrigin.longitude } : undefined,
    [chargerOrigin],
  );

  const handleResetToCharger = () => {
    setInputKey((key) => key + 1);
    onResetToCharger();
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-row justify-between items-baseline'>
        <Label
          text={t('nearby_chargers_address')}
          type={LabelType.LABEL_S}
          color={ColorType.BLACK}
        />
        {canResetToCharger && (
          // A real button so it is reachable and usable from the keyboard
          <button
            type='button'
            onClick={handleResetToCharger}
            className={`${LabelType.LABEL_XS} text-brand2 hover:underline rounded outline-none focus-visible:ring-2 focus-visible:ring-brand2`}
          >
            {t('nearby_chargers_use_charger_location')}
          </button>
        )}
      </div>
      <GeoapifyGeocoderAutocomplete
        key={inputKey}
        value={chargerOrigin?.label ?? ''}
        placeholder={t('nearby_chargers_address_placeholder')}
        debounceDelay={ADDRESS_DEBOUNCE_MS}
        limit={ADDRESS_SUGGESTION_LIMIT}
        skipIcons
        allowNonVerifiedHouseNumber
        biasByProximity={biasByProximity}
        placeSelect={(place: GeoapifyPlace | null) => onOriginSelect(toFinderOrigin(place))}
        onUserInput={onUnresolvedInput}
      />
    </div>
  );
};
